/**
 * DataBest — PerfAiNarrative
 * Collapsible panel: "Generate coaching narrative" via Claude.
 * Uses supervisor tone for supervisors+, self-view tone for caregivers.
 */
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Sparkles, Loader2, ChevronDown, ChevronUp, RefreshCw } from "lucide-react";
import { caregiverPerfApi } from "../api/caregiverPerfApi";

const PerfAiNarrative = ({ caregiverId, period, selfView = false, onNarrativeChange }) => {
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [narrative, setNarrative] = useState(null);
    const [model, setModel] = useState(null);

    // Reset when caregiver or period changes so the parent doesn't ship a stale
    // narrative into the PDF export.
    useEffect(() => {
        setNarrative(null);
        setModel(null);
        setOpen(false);
        if (onNarrativeChange) onNarrativeChange(null);
    }, [caregiverId, period?.period_start, period?.period_end, onNarrativeChange]);

    const generate = async () => {
        setLoading(true);
        try {
            const r = await caregiverPerfApi.generateNarrative(caregiverId, period);
            setNarrative(r.text);
            setModel(r.model);
            setOpen(true);
            if (onNarrativeChange) onNarrativeChange(r.text);
        } catch (err) {
            toast.error(err?.message || "Could not generate narrative");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div
            className="rounded-2xl border border-slate-200 bg-white shadow-sm"
            data-testid="perf-ai-narrative"
        >
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                className="flex w-full items-center justify-between border-b border-slate-100 px-4 py-2.5 text-left hover:bg-slate-50"
            >
                <p className="inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-slate-500">
                    <Sparkles className="h-3.5 w-3.5 text-brand-blue-700" />
                    AI Coaching Narrative
                    {selfView && (
                        <span className="rounded-full bg-brand-green-100 px-1.5 py-0 text-[9px] font-bold text-brand-green-800">
                            Self view
                        </span>
                    )}
                </p>
                {open ? (
                    <ChevronUp className="h-3.5 w-3.5 text-slate-400" />
                ) : (
                    <ChevronDown className="h-3.5 w-3.5 text-slate-400" />
                )}
            </button>
            {open && (
                <div className="space-y-3 p-4">
                    {!narrative ? (
                        <div className="text-center">
                            <p className="mb-2 text-xs text-slate-500">
                                {selfView
                                    ? "Generate a personalized coaching note based on your own metrics."
                                    : "Generate an evidence-based coaching narrative from the KPIs above (strengths → opportunities → suggested coaching)."}
                            </p>
                            <button
                                type="button"
                                onClick={generate}
                                disabled={loading}
                                data-testid="perf-ai-generate-btn"
                                className="inline-flex items-center gap-1.5 rounded-lg bg-brand-blue-700 px-3 py-1.5 text-xs font-bold text-white hover:bg-brand-blue-800 disabled:opacity-60"
                            >
                                {loading ? (
                                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                ) : (
                                    <Sparkles className="h-3.5 w-3.5" />
                                )}
                                {loading ? "Generating…" : "Generate narrative"}
                            </button>
                        </div>
                    ) : (
                        <>
                            <div className="whitespace-pre-wrap text-sm leading-relaxed text-slate-700">
                                {narrative}
                            </div>
                            <div className="flex items-center justify-between border-t border-slate-100 pt-2 text-[10px] text-slate-400">
                                <span className="font-mono">{model}</span>
                                <button
                                    type="button"
                                    onClick={generate}
                                    disabled={loading}
                                    data-testid="perf-ai-regenerate-btn"
                                    className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2 py-1 text-[10px] font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-60"
                                >
                                    {loading ? (
                                        <Loader2 className="h-3 w-3 animate-spin" />
                                    ) : (
                                        <RefreshCw className="h-3 w-3" />
                                    )}
                                    Regenerate
                                </button>
                            </div>
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

export default PerfAiNarrative;
